import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { SingleValue } from 'react-select';

import { Button, FormField, Select } from '@revelio/ui';

import type { DeliveryFormat } from '../../data-builder.store';

type DownloadConfigModalProps = {
  isOpen: boolean;
  onClose: () => void;
  initialValue: DeliveryFormat | null;
  onSave: (format: DeliveryFormat) => void;
};

type DownloadConfigForm = {
  format: DeliveryFormat;
};

const formatOptions: { value: DeliveryFormat; label: string }[] = [
  { value: 'csv', label: 'CSV' },
  { value: 'parquet', label: 'Parquet' },
  { value: 'excel', label: 'Excel' },
];

export const DownloadConfigModal = ({
  isOpen,
  onClose,
  initialValue,
  onSave,
}: DownloadConfigModalProps) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<DownloadConfigForm>({
    defaultValues: {
      format: initialValue ?? 'excel',
    },
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    reset({
      format: initialValue ?? 'excel',
    });
  }, [initialValue, isOpen, reset]);

  const onSubmit = handleSubmit((value) => {
    onSave(value.format);
    onClose();
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="text.primary">Download Configuration</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormField
            label="File format"
            errors={
              errors.format?.message !== undefined
                ? [errors.format.message]
                : undefined
            }
          >
            <Controller
              control={control}
              name="format"
              rules={{ required: 'Format is required' }}
              render={({ field }) => (
                <Select
                  options={formatOptions}
                  value={formatOptions.find(
                    (option) => option.value === field.value
                  )}
                  onChange={(
                    selectedOption: SingleValue<{
                      value: DeliveryFormat;
                      label: string;
                    }>
                  ) => {
                    field.onChange(selectedOption?.value ?? 'excel');
                  }}
                />
              )}
            />
          </FormField>
        </ModalBody>

        <ModalFooter gap="3">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button color="primary" onClick={onSubmit} disabled={isSubmitting}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
